import ColorJS, { ColorTypes } from 'colorjs.io'

/**
 * Constants for color adjustments
 */
const LIGHTNESS_ADJ = 0.55
const BORDER_REDUCTION = 0.22

/**
 * Luminance bands with their respective adjustment configurations
 */
const LUMINANCE_BANDS = {
  DARK: {
    maxLuminance: 0.25,
    adjustments: {
      foreground: LIGHTNESS_ADJ * 2.2,
      border: LIGHTNESS_ADJ * 2 * BORDER_REDUCTION,
      hoverBorder: LIGHTNESS_ADJ * 2 * BORDER_REDUCTION * 4,
      direction: 'lighter',
    },
  },
  MID: {
    maxLuminance: 0.8,
    adjustments: {
      foreground: LIGHTNESS_ADJ * 1.2,
      border: LIGHTNESS_ADJ * BORDER_REDUCTION * 2,
      hoverBorder: LIGHTNESS_ADJ * BORDER_REDUCTION * 4,
      direction: 'darker',
    },
  },
  LIGHT: {
    maxLuminance: Infinity,
    adjustments: {
      foreground: LIGHTNESS_ADJ,
      border: LIGHTNESS_ADJ * BORDER_REDUCTION,
      hoverBorder: LIGHTNESS_ADJ * BORDER_REDUCTION * 2,
      direction: 'darker',
    },
  },
} as const

/**
 * Adjust color lightness in oklch color space
 *
 * @param color The color to adjust
 * @param direction Whether to make the color lighter or darker
 * @param factor The adjustment factor (higher = more intense change)
 * @returns The adjusted color as a string
 */
const adjustColorLightness = (
  color: ColorJS,
  direction: 'lighter' | 'darker',
  factor: number
): string => {
  const clone = color.clone()
  const currentL = clone.get('oklch.l')
  // Floor L so pure black (oklch.l = 0) doesn't stay black after multiplication.
  const baseL = direction === 'lighter' ? Math.max(currentL, 0.4) : currentL
  const newL =
    direction === 'lighter' ? baseL * (1 + factor) : baseL * (1 - factor)

  clone.set('oklch.l', newL)
  return clone.toString()
}

/**
 * Determines the luminance band based on the luminance value
 *
 * @param luminance The luminance value of the color
 * @returns The corresponding luminance band
 */
const getBandForLuminance = (luminance: number) => {
  if (luminance < LUMINANCE_BANDS.DARK.maxLuminance) {
    return LUMINANCE_BANDS.DARK
  } else if (luminance < LUMINANCE_BANDS.MID.maxLuminance) {
    return LUMINANCE_BANDS.MID
  } else {
    return LUMINANCE_BANDS.LIGHT
  }
}

/**
 * Derives accessible colors (background, foreground, border, hover border)
 * based on the base color's luminance.
 *
 * @param baseColor The base color to derive colors from
 * @returns Object containing background, foreground, border and hoverBorder colors
 */
export const deriveAccessibleColors = (baseColor: ColorTypes) => {
  const color = new ColorJS(baseColor).to('oklch')
  const luminance = color.luminance
  const band = getBandForLuminance(luminance)

  const {
    direction,
    foreground: fgFactor,
    border: borderFactor,
    hoverBorder: hoverFactor,
  } = band.adjustments

  return {
    background: baseColor.toString(),
    foreground: adjustColorLightness(color, direction, fgFactor),
    border: adjustColorLightness(color, direction, borderFactor),
    hoverBorder: adjustColorLightness(color, direction, hoverFactor),
  }
}
